import { Params } from '@angular/router';
import { safeSessionStorageGetItem } from './safe-storage.util';

export const WEB_EMBED_MODE_KEY = 'web_embed_mode';

const PUBLIC_HELP_GUIDE_PATHS = [
  '/web/help-center',
  '/web/help-guide',
] as const;

function extractPathname(pathOrUrl: string): string {
  return pathOrUrl.split('?')[0].split('#')[0];
}

function getPathname(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.pathname;
}

function normalizePath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }

  return path;
}

function readSearchParams(source?: string): URLSearchParams {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  if (!source) {
    return new URLSearchParams(window.location.search);
  }

  const query = source.includes('?')
    ? source.slice(source.indexOf('?') + 1)
    : source;

  return new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
}

function hasValue(value: string | null | undefined): boolean {
  return value !== undefined && value !== null && value !== '';
}

/** True when authorization, device type, and xPortal are all present. */
export function hasWebEmbedQueryParams(
  queryParams?: Params | URLSearchParams | string
): boolean {
  let params: URLSearchParams;

  if (queryParams instanceof URLSearchParams) {
    params = queryParams;
  } else if (typeof queryParams === 'string') {
    params = readSearchParams(queryParams);
  } else if (queryParams) {
    params = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    });
  } else {
    params = readSearchParams();
  }

  const hasAuthorization = hasValue(params.get('authorization'));
  const hasDeviceType =
    hasValue(params.get('deviceType')) || hasValue(params.get('device_type'));
  const hasXPortal = hasValue(params.get('xPortal'));

  return hasAuthorization && hasDeviceType && hasXPortal;
}

export function isPublicHelpGuideRoute(pathname?: string): boolean {
  const path = normalizePath(extractPathname(pathname ?? getPathname()));

  return PUBLIC_HELP_GUIDE_PATHS.some(publicPath => {
    return path === publicPath || path.startsWith(`${publicPath}/`);
  });
}

export function isWebEmbedModeActive(): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }

  return safeSessionStorageGetItem(WEB_EMBED_MODE_KEY) === '1';
}

/**
 * Help-center iframe embeds and other web-embed sessions must not play
 * push-notification sounds or bootstrap admin notification plumbing.
 */
export function shouldSuppressNotificationSounds(pathOrUrl?: string): boolean {
  if (isPublicHelpGuideRoute(pathOrUrl)) {
    return true;
  }

  if (isWebEmbedModeActive()) {
    return true;
  }

  if (pathOrUrl && hasWebEmbedQueryParams(pathOrUrl)) {
    return true;
  }

  return hasWebEmbedQueryParams();
}

export function arePushNotificationsSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator
  );
}

export function shouldRegisterNotificationServiceWorker(): boolean {
  return arePushNotificationsSupported() && !shouldSuppressNotificationSounds();
}
